/**
 * Calculates the totals of measurements
 * @param {Array} measures - Array of measurements
 * @returns {Object} Object with sums of totals, percentages and units
 */
export const calculateTotals = measures => {
  if (!measures || measures.length === 0) {
    return {
      totalSum: 0,
      percentageSum: 0,
      unitsSum: 0,
      previousUnitsSum: 0,
      previousPercentageSum: 0,
    }
  }

  const sums = measures.reduce(
    (acc, measure) => {
      // Calculate total for this measurement
      const dimensions = []
      if (measure.depth && measure.depth !== 0) dimensions.push(measure.depth)
      if (measure.width && measure.width !== 0) dimensions.push(measure.width)
      if (measure.height && measure.height !== 0)
        dimensions.push(measure.height)
      const total =
        dimensions.length > 0
          ? dimensions.reduce((a, b) => a * b, 1) * measure.quantity
          : 0

      // Get certification values
      const cert_current = measure.cert_current || 0
      const cert_previous = measure.cert_previous || 0

      return {
        totalSum: acc.totalSum + total,
        unitsSum: acc.unitsSum + cert_current,
        previousUnitsSum: acc.previousUnitsSum + cert_previous,
      }
    },
    {
      totalSum: 0,
      unitsSum: 0,
      previousUnitsSum: 0,
    },
  )

  // Calculate percentage totals based on the proportion of sums
  const percentageSum =
    sums.totalSum > 0 ? (sums.unitsSum / sums.totalSum) * 100 : 0
  const previousPercentageSum =
    sums.totalSum > 0 ? (sums.previousUnitsSum / sums.totalSum) * 100 : 0

  return {
    ...sums,
    percentageSum,
    previousPercentageSum,
  }
}

/**
 * Calculates the dimensions of a measurement as a string
 * @param {Object} measure - Measurement object with depth, width, height
 * @returns {string} String with formatted dimensions (e.g., "2 × 3 × 4" or "-")
 */
export const calculateDimensions = measure => {
  const { depth, width, height } = measure
  const dimensions = []

  if (depth && depth !== 0) dimensions.push(depth)
  if (width && width !== 0) dimensions.push(width)
  if (height && height !== 0) dimensions.push(height)

  return dimensions.length > 0 ? dimensions.join(" × ") : "-"
}

/**
 * Calculates the total (product) of the dimensions of a measurement
 * @param {Object} measure - Measurement object with depth, width, height
 * @returns {number} Product of non-zero dimensions
 */
export const calculateDimensionTotal = measure => {
  const { depth, width, height, quantity } = measure
  const dimensions = []

  if (depth && depth !== 0) dimensions.push(depth)
  if (width && width !== 0) dimensions.push(width)
  if (height && height !== 0) dimensions.push(height)

  if (dimensions.length === 0 && (!quantity || quantity === 0)) {
    return 0
  }

  const qty = quantity === 0 ? 1 : quantity
  return dimensions.length > 0
    ? dimensions.reduce((acc, curr) => acc * curr, 1) * qty
    : qty
}

/**
 * Calculates the certification percentage based on cert_current and the total dimensions
 * @param {Object} measure - Measurement object with depth, width, height, cert_current
 * @returns {number} Rounded percentage
 */
export const calculatePercentage = measure => {
  const { cert_current } = measure
  const total = calculateDimensionTotal(measure)

  if (total === 0 || !cert_current) return 0

  return (cert_current / total) * 100
}

/**
 * Validates and clamps the certified units to not exceed the available total (total - cert_previous)
 * @param {number} certCurrent - The cert_current value being validated
 * @param {Object} measure - Measurement object with depth, width, height, cert_previous
 * @returns {number} Clamped cert_current value
 */
export const validateCertCurrent = (certCurrent, measure) => {
  const total = calculateDimensionTotal(measure)
  const certPrevious = measure.cert_previous || 0
  const available = Math.max(total - certPrevious, 0)

  if (total === 0) return 0
  if (certCurrent < 0) return 0
  if (certCurrent > available) return available

  return certCurrent
}

/**
 * Updates a measurement with validated cert_current value
 * @param {Object} measure - Measurement object
 * @param {number} newCertCurrent - New cert_current value to validate and apply
 * @returns {Object} Updated measurement object with validated cert_current
 */
export const updateMeasureWithValidation = (measure, newCertCurrent) => {
  const validatedCertCurrent = validateCertCurrent(newCertCurrent, measure)
  return {
    ...measure,
    cert_current: validatedCertCurrent,
  }
}
